import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { MedicationRequest } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface RenewPrescriptionsParams {
  medicationRequestIds: string[];
  drugIdentifiers: string[];
}

interface RenewPrescriptionsResponse {
  renewedPrescriptions: MedicationRequest[];
  failedRenewals: MedicationRequest[];
  failedNewOrders: string[];
}

export const useRenewPrescriptions = ({
  patientId,
  encounterId,
}: {
  patientId: string;
  encounterId: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<RenewPrescriptionsResponse, RenewPrescriptionsParams>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Prescriptions.RENEW_PRESCRIPTIONS({
        patientId,
        encounterId,
      }),
      mutationFn: async (params) => {
        const response = await axiosInstance.post(
          "/rx/refills/renew-prescriptions",
          {
            ...params,
            encounterId,
          },
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Prescriptions.DEFAULT(
            patientId,
            encounterId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
            patientId,
            encounterId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.Chart.Prescriptions.RENEWABLE_PRESCRIPTIONS({
              patientId,
              encounterId,
            }),
        });
      },
    },
  });
};
